import { memo, useCallback, useId, useRef, useState } from 'react'
import { Sparkles } from 'lucide-react'
import { AssistBar } from '@/components/patterns/AssistBar'
import { TITLE_STYLE_LABELS, usePrepackCopySuggestions } from './usePrepackCopySuggestions'
import { MAX_TAGLINE_LENGTH, limitTagline, taglineLength } from './prepackTagline'

// Quick actions ask the title service for one title style (#543); Suggest
// asks for a mix across styles, and the Fun and Energetic tone presets send a
// free-text direction instead. `instruction` is what AssistBar shows; text
// typed in the prompt goes to the service as a free-text direction too.
const styleAction = (style, instruction) => ({ key: style, label: TITLE_STYLE_LABELS[style], style, instruction })
const toneAction = (key, label, direction) => ({ key, label, direction, instruction: direction })

const TITLE_ACTIONS = [
  { key: 'suggest', label: 'Suggest', style: '', instruction: '' },
  styleAction('wordplay', 'Rhyme, alliteration or a pun'),
  styleAction('saying', 'A familiar saying with a sports twist'),
]

const TITLE_OVERFLOW_ACTIONS = [
  styleAction('local', 'A place, nickname or chant the legs involve'),
  styleAction('story', 'The legs as one connected story'),
  styleAction('descriptive', 'Plainly what the pack is'),
  toneAction('fun', 'Fun', 'Playful and witty'),
  toneAction('energetic', 'Energetic', 'High-energy hype'),
]

// The assist bar (menu, buttons, icons) doesn't change while the operator
// types a title; memoized with stable handlers it skips those renders.
const MemoAssistBar = memo(AssistBar)

const inputClassName = 'w-full rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] px-3 py-2 text-xs text-[var(--wf-color-text)] outline-none transition-colors focus:border-[var(--prepacks-accent-text)] disabled:opacity-50'

function AiBadge() {
  return (
    <span className="ml-2 inline-flex items-center gap-1 rounded bg-[var(--wf-color-action-soft)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--prepacks-accent-text)]">
      <Sparkles className="h-2.5 w-2.5" /> AI
    </span>
  )
}

/**
 * Title and tagline inputs sharing one AI AssistBar; suggestions are matched
 * pairs. `aiStatus` is 'enabled', 'disabled' or 'unknown'; only 'enabled'
 * allows automatic suggestions.
 */
export default function PrepackCopyFields({
  orgSlug,
  regulation,
  legs = [],
  title = '',
  titleSource = 'operator',
  onTitleChange,
  tagline = '',
  taglineSource = 'operator',
  onTaglineChange,
  disabled = false,
  aiStatus = 'unknown',
  aiUnavailableReason = '',
  autoSuggest = false,
  skipLegKey = null,
}) {
  const [prompt, setPrompt] = useState('')
  const titleId = useId()
  const taglineId = useId()
  const aiDisabled = aiStatus === 'disabled'
  const suggestions = usePrepackCopySuggestions({
    orgSlug,
    regulation,
    legs,
    fields: {
      title: { value: title, source: titleSource, onChange: onTitleChange },
      tagline: { value: tagline, source: taglineSource, onChange: onTaglineChange },
    },
    disabled,
    manualEnabled: !aiDisabled,
    autoSuggest: autoSuggest && aiStatus === 'enabled',
    skipLegKey,
  })
  const { busy, error, results, canGenerate } = suggestions
  // Latest hook result for the handlers below, which stay the same across renders.
  const suggestionsRef = useRef(suggestions)
  suggestionsRef.current = suggestions

  const handleRequest = useCallback((instruction, { source, action } = {}) => {
    if (source === 'action') {
      suggestionsRef.current.request(action?.direction || '', { style: action?.style || '' })
      return
    }
    suggestionsRef.current.request(String(instruction || '').trim())
  }, [])
  const handleApplyResult = useCallback((_text, result) => suggestionsRef.current.applyResult(result), [])

  return (
    <div className="flex w-full flex-col gap-3">
      <div>
        <label htmlFor={titleId} className="mb-1 block text-xs text-[var(--prepacks-muted)]">
          Title
          {titleSource === 'ai' ? <AiBadge /> : null}
        </label>
        <input
          id={titleId}
          type="text"
          value={title}
          onChange={(event) => suggestions.noteManualEdit('title', event.target.value)}
          disabled={disabled}
          aria-label="Title"
          placeholder="e.g. Eagles same-game parlay"
          className={inputClassName}
        />
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between gap-3">
          <label htmlFor={taglineId} className="text-xs text-[var(--prepacks-muted)]">
            Tagline
            {taglineSource === 'ai' && tagline ? <AiBadge /> : null}
          </label>
          <span className="text-[10px] tabular-nums text-[var(--prepacks-muted)]" aria-hidden="true">
            {taglineLength(tagline)}/{MAX_TAGLINE_LENGTH}
          </span>
        </div>
        <input
          id={taglineId}
          type="text"
          value={tagline}
          onChange={(event) => suggestions.noteManualEdit('tagline', limitTagline(tagline, event.target.value))}
          disabled={disabled}
          aria-label="Tagline"
          aria-description={`Shown under the title, one line of up to ${MAX_TAGLINE_LENGTH} characters`}
          placeholder="e.g. Two stars, one long night in Philly"
          className={inputClassName}
        />
      </div>

      <MemoAssistBar
        id={`${titleId}-assist`}
        label="AI title and tagline suggestions"
        prompt={prompt}
        promptLabel="Describe the tone or angle"
        placeholder="Describe the tone or angle (e.g. bold, concise, name the venue)"
        onPromptChange={setPrompt}
        onRequest={handleRequest}
        actions={TITLE_ACTIONS}
        overflowActions={TITLE_OVERFLOW_ACTIONS}
        pending={busy}
        pendingLabel="Generating title and tagline options…"
        disabled={!canGenerate || disabled || aiDisabled}
        results={results}
        resultsLabel="AI title and tagline suggestions"
        onApplyResult={handleApplyResult}
        applyLabel="Use"
      />

      {aiDisabled && aiUnavailableReason ? (
        <p className="text-xs text-[var(--prepacks-muted)]">{aiUnavailableReason}</p>
      ) : null}

      {error ? (
        <p role="alert" className="text-xs text-[var(--wf-color-danger)]">
          {error}
        </p>
      ) : null}
    </div>
  )
}
